import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppState } from '../../context/AppStateContext';
import { calculate8Level, calculateMeanScore, getLevelData } from '../../utils/cbcUtils';
import FilterDropdown from '../../components/common/FilterDropdown';

import {
    ClockIcon,
    CalendarIcon,
    AcademicCapIcon,
    BanknotesIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import PaymentModal from '../../components/parent/PaymentModal';
import CompareControls from '../../components/dashboard/CompareControls';
import FloatingManualButton from '../../components/common/FloatingManualButton';

const ParentDashboard = () => {
    const navigate = useNavigate();
    const { get, post } = useApi();
    const { performanceBands } = useAppState();
    const [selectedChild, setSelectedChild] = useState(null);
    const [comparisonFilters, setComparisonFilters] = useState({});
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedTerm, setSelectedTerm] = useState('');
    const [examTypeFilter, setExamTypeFilter] = useState('combined');

    // Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedMobileDate, setSelectedMobileDate] = useState(null);

    const getDynamicGreeting = useCallback(() => {
        const hour = new Date().toLocaleString("en-US", { timeZone: "Africa/Nairobi", hour: 'numeric', hour12: false });
        const hr = parseInt(hour, 10);
        if (hr >= 0 && hr < 12) return 'Good morning';
        if (hr >= 12 && hr < 17) return 'Good afternoon';
        if (hr >= 17 && hr < 22) return 'Good evening';
        return 'Welcome back';
    }, []);

    const { data: terms } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (terms && terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current) || terms[0];
            setSelectedTerm(currentTerm.id);
        }
    }, [terms, selectedTerm]);

    // ── Parent profile (cached for 30 min — rarely changes) ──────────────────
    const { data: parent, isLoading: parentLoading } = useQuery({
        queryKey: ['parent', 'me'],
        queryFn: () => get('/api/parents/me/'),
        staleTime: 30 * 60 * 1000,
    });

    // ── Auto-selection & Persistence Logic ───────────────────────────────────
    useEffect(() => {
        if (!parentLoading && parent?.children?.length > 0 && !selectedChild) {
            const savedChildId = localStorage.getItem('parent_selected_child_id');
            const foundChild = parent.children.find(c => String(c.id) === savedChildId);
            
            if (foundChild) {
                setSelectedChild(foundChild);
            } else {
                setSelectedChild(parent.children[0]);
            }
        }
    }, [parent, parentLoading, selectedChild]);

    useEffect(() => {
        if (selectedChild?.id) {
            localStorage.setItem('parent_selected_child_id', String(selectedChild.id));
        }
    }, [selectedChild]);

    // ── Per-child data (keyed by childId — switching children uses cache) ─────
    const childId = selectedChild?.id;
    const { data: childData } = useQuery({
        queryKey: ['child-data', childId, selectedTerm, examTypeFilter],
        queryFn: async () => {
            const termQuery = selectedTerm ? `?term_id=${selectedTerm}` : '';
            const effectiveExamType = examTypeFilter === 'combined' ? '' : examTypeFilter;
            const examTypeQuery = effectiveExamType ? `&exam_type=${effectiveExamType}` : '';
            const summaryQuery = termQuery ? `${termQuery}${examTypeQuery}` : (effectiveExamType ? `?exam_type=${effectiveExamType}` : '');
            
            const [activities, finances, calendar, metrics] = await Promise.all([
                get(`/api/parents/child-activities/${childId}/`),
                get(`/api/parents/child-finances/${childId}/${termQuery}`),
                get(`/api/parents/child-calendar/${childId}/`),
                get(`/api/cbc/competency-assessments/summary/${childId}/${summaryQuery}`),
            ]);
            return { activities, finances, calendar, metrics };
        },
        enabled: !!childId,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // ── Comparison data (keyed by childId + filters) ─────────────────────────
    const effectiveExamTypeForComp = examTypeFilter === 'combined' ? '' : examTypeFilter;
    const queryParams = new URLSearchParams({ ...comparisonFilters, ...(effectiveExamTypeForComp ? { exam_type: effectiveExamTypeForComp } : {}) }).toString();
    const { data: childComparisonData = [], isFetching: compLoading } = useQuery({
        queryKey: ['child-comparison', childId, comparisonFilters, examTypeFilter],
        queryFn: () => get(`/api/cbc/competency-assessments/performance-comparison/${childId}/${queryParams ? `?${queryParams}` : ''}`),
        enabled: !!childId,
        staleTime: 5 * 60 * 1000,
    });

    // Destructure child data with safe defaults
    const childActivities = childData?.activities ?? null;
    const childFinances = childData?.finances ?? null;
    const childCalendarData = childData?.calendar ?? null;
    const childMetrics = childData?.metrics ?? null;
    const loading = parentLoading;

    const handlePayEventFee = useCallback(async (event) => {
        try {
            await post('/api/events/pay-fee/', {
                student_id: selectedChild.id,
                event_id: event.id
            });
            alert(`Fee for ${event.title} has been processed. A fee record for KES ${event.cost} has been created.`);
        } catch (error) {
            console.error('Error paying event fee:', error);
            alert('Could not process payment. Please try again or contact administration.');
        }
    }, [post, selectedChild?.id]);

    const queryClient = useQueryClient();

    // After a payment, invalidate child-data so finances re-fetch
    const handlePaymentSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['child-data', childId] });
        queryClient.invalidateQueries({ queryKey: ['child-comparison', childId] });
    }, [queryClient, childId]);

    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Helper to formatting date as YYYY-MM-DD using local time
        const formatLocal = (y, m, d) => {
            const mm = String(m + 1).padStart(2, '0');
            const dd = String(d).padStart(2, '0');
            return `${y}-${mm}-${dd}`;
        };

        const firstDay = new Date(year, month, 1).getDay();
        const days = new Date(year, month + 1, 0).getDate();

        const calendarDays = [];
        for (let i = 0; i < firstDay; i++) {
            calendarDays.push({ day: null, fullDate: null });
        }
        for (let i = 1; i <= days; i++) {
            calendarDays.push({
                day: i,
                fullDate: formatLocal(year, month, i)
            });
        }
        return calendarDays;
    }, [currentDate]);

    const eventDates = useMemo(() => {
        const dates = {};
        if (!childCalendarData) return dates;

        const seenIds = new Set();

        [
            ...(childCalendarData.assignments || []).map(a => ({ ...a, type: 'Assignment', uid: `asgn-${a.id}` })),
            ...(childCalendarData.quizzes || []).map(q => ({ ...q, type: 'Quiz', uid: `quiz-${q.id}` })),
            ...(childCalendarData.events || []).map(e => ({
                ...e,
                type: 'School Event',
                due_date: e.start_datetime,
                is_event: true,
                uid: `event-${e.id}`
            }))
        ].forEach(ev => {
            if (ev.due_date && !seenIds.has(ev.uid)) {
                seenIds.add(ev.uid);
                const datePart = ev.due_date.split('T')[0];
                if (!dates[datePart]) dates[datePart] = [];
                dates[datePart].push(ev);
            }
        });

        return dates;
    }, [childCalendarData]);

    const changeMonth = (offset) => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18216D]"></div>
            </div>
        );
    }

    if (!parent || !parent.children || parent.children.length === 0) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
                <div className="max-w-2xl w-full">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/10 p-12 text-center border border-slate-100 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full -mr-16 -mt-16"></div>
                        <div className="h-24 w-24 bg-indigo-50 rounded-3xl flex items-center justify-center mx-auto mb-8 relative z-10">
                            <i className="fas fa-users-viewfinder text-4xl text-[#18216D]"></i>
                        </div>
                        <h2 className="text-3xl font-black text-[#18216D] mb-4">Awaiting Connection</h2>
                        <p className="text-slate-500 font-medium text-lg leading-relaxed mb-10">
                            Welcome to the Kianda Parent Portal. Your account is active, but no scholar profiles have been linked to your profile yet by the administration.
                        </p>
                        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100 flex items-start gap-4 text-left">
                            <i className="fas fa-circle-info text-amber-600 mt-1"></i>
                            <div>
                                <p className="text-sm font-black text-amber-900 uppercase tracking-wider mb-1">What's Next?</p>
                                <p className="text-sm text-amber-800/80 font-medium">
                                    Please contact the school administration office to have your children's profiles securely connected to your account.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">

            <main className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6 md:py-10 flex-1 space-y-8 md:space-y-10">
                {/* Redesigned Header */}
                <header className="relative overflow-hidden bg-[#18216D] rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Parent Portal</p>
                            <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter">{getDynamicGreeting()}, {parent?.first_name || 'Guardian'}</h1>
                            <div className="h-2 w-24 bg-[#FFC425] rounded-full mt-6"></div>
                        </div>
                    </div>
                </header>

                <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    {/* On mobile, hide student list if only one child. On desktop, always show. */}
                    <div className={`flex flex-col mb-4 md:mb-0 ${parent.children.length === 1 ? 'hidden md:flex' : 'flex'}`}>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Student List</label>
                        <div className="flex overflow-x-auto gap-3 pb-4 pt-2 px-1 scrollbar-hide snap-x">
                            {parent.children.map(child => (
                                <button
                                    key={child.id}
                                    onClick={() => setSelectedChild(child)}
                                    className={`relative px-5 py-3 md:px-6 md:py-4 rounded-[1.5rem] text-xs font-black uppercase tracking-widest transition-all duration-300 shrink-0 snap-start flex items-center gap-3 overflow-hidden group ${selectedChild?.id === child.id
                                        ? 'bg-[#18216D] text-white border-2 border-[#18216D]'
                                        : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-[#18216D] border-2 border-slate-200 hover:border-[#18216D]/30 shadow-sm'}`}
                                >
                                    <div className={`relative h-8 w-8 rounded-full flex items-center justify-center transition-colors ${selectedChild?.id === child.id ? 'bg-white/10 text-[#FFC425]' : 'bg-slate-100 text-slate-500 group-hover:bg-[#18216D]/10 group-hover:text-[#18216D]'}`}>
                                        <i className="fas fa-user-graduate text-[10px]"></i>
                                    </div>
                                    <span className="relative z-10">{child.first_name} {child.last_name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {selectedChild && (
                        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 w-full md:w-auto shrink-0 z-50">
                            {/* Filters — side by side on mobile */}
                            <div className="flex flex-row gap-2 flex-1 md:flex-none">
                                <div className="bg-white border border-slate-200 px-3 md:px-4 py-2 md:py-3 rounded-2xl shadow-sm flex flex-col items-start shrink-0 z-50 flex-1 md:flex-none">
                                    <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Term Filter</label>
                                    <FilterDropdown
                                        value={selectedTerm}
                                        onChange={(val) => setSelectedTerm(val)}
                                        options={terms?.map(term => ({ value: term.id, name: term.name })) || []}
                                        placeholder="All Terms"
                                        className="w-full md:w-40"
                                    />
                                </div>
                                <div className="bg-white border border-slate-200 px-3 md:px-4 py-2 md:py-3 rounded-2xl shadow-sm flex flex-col items-start shrink-0 z-50 flex-1 md:flex-none">
                                    <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Assessment</label>
                                    <FilterDropdown
                                        value={examTypeFilter}
                                        onChange={(val) => setExamTypeFilter(val)}
                                        options={[
                                            { name: 'Combined Average', value: 'combined' },
                                            { name: 'Mid-Term Exam', value: 'mid-term' },
                                            { name: 'End-Term Exam', value: 'end-term' },
                                            { name: 'Formative Assessments', value: 'formative' }
                                        ]}
                                        placeholder="Select Assessment"
                                        className="w-full md:w-48"
                                    />
                                </div>
                            </div>
                            {/* Amber card — shows student name on mobile when only 1 child */}
                            <div className="bg-[#FFC425]/10 border border-[#FFC425]/20 px-4 py-2 md:py-3 rounded-2xl flex flex-col justify-center shrink-0">
                                {parent.children.length === 1 && (
                                    <p className="text-[11px] font-black uppercase tracking-widest text-[#18216D] mb-0.5 md:hidden">
                                        {selectedChild.first_name} {selectedChild.last_name}
                                    </p>
                                )}
                                <p className="text-[10px] md:text-xs font-black uppercase tracking-widest text-[#B48A1B]">
                                    {selectedChild.grade_name || selectedChild.grade ? (selectedChild.grade_name || `Grade ${selectedChild.grade}`) : 'Scholar'}
                                    {selectedChild.stream_name ? ` (${selectedChild.stream_name})` : ''}
                                </p>
                                <p className="text-[9px] md:text-[10px] font-bold text-[#18216D]">{selectedChild.student_id}</p>
                            </div>
                        </div>
                    )}
                </div>

                {selectedChild && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Competency Summary Top Row */}
                        <div className="mb-8 bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 p-6 md:p-8 sm:p-10">
                            {/* Mobile: clickable link to report page. Desktop: plain heading. */}
                            <button
                                onClick={() => navigate(`/parent/child/${selectedChild.id}/progress`)}
                                className="flex md:hidden items-center gap-2 text-left group"
                            >
                                <ChartBarIcon className="h-5 w-5 text-[#FFC425] shrink-0" />
                                <span className="text-base font-black text-[#18216D] italic group-hover:underline">Overall Academic Progress</span>
                                <i className="fas fa-chevron-right text-[10px] text-[#FFC425]"></i>
                            </button>
                            <h3 className="hidden md:flex text-xl font-black text-[#18216D] mb-8 items-center gap-3 italic">
                                <ChartBarIcon className="h-6 w-6 text-[#FFC425]" />
                                Overall Academic Progress
                            </h3>
                            {childMetrics ? (
                                <div className="flex md:grid md:grid-cols-4 gap-4 md:gap-6 overflow-x-auto pb-4 md:pb-0 scrollbar-hide snap-x">
                                    {[
                                        { level: 'EE', label: 'Exceeding Expectation', icon: 'fa-crown' },
                                        { level: 'ME', label: 'Meeting Expectation', icon: 'fa-check-double' },
                                        { level: 'AE', label: 'Approaching Expectation', icon: 'fa-shoe-prints' },
                                        { level: 'BE', label: 'Below Expectation', icon: 'fa-triangle-exclamation' },
                                    ].map((stat, i) => {
                                        const data = getLevelData(stat.level);
                                        const val = childMetrics.by_level?.[stat.level] || 0;
                                        return (
                                            <div key={i} className={`${data.bg} p-6 rounded-[2rem] border border-transparent hover:border-slate-200 transition-all text-center group min-w-[140px] md:min-w-0 snap-center`}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="h-8 w-8 bg-white/80 rounded-xl flex items-center justify-center shadow-sm">
                                                        <i className={`fas ${stat.icon} ${data.color.replace('text-', 'text-')} text-xs`}></i>
                                                    </div>
                                                </div>
                                                <div className={`text-4xl font-black ${data.color} tracking-tighter`}>{val}</div>
                                                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-2 leading-tight">{stat.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-slate-300 font-bold uppercase tracking-widest text-xs">Aggregating success metrics...</div>
                            )}
                        </div>


                        {/* Row 2: Finance & Mastery Hub */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                            {/* Finance Card (2/3) */}
                            <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 p-6 md:p-8 flex flex-col justify-between group overflow-hidden relative">
                                <div className="absolute -top-12 -right-12 w-48 h-48 bg-emerald-50 rounded-full blur-3xl opacity-50 group-hover:scale-110 transition-transform"></div>
                                <div className="relative z-10 flex items-center justify-between mb-8">
                                    <div className="flex items-center gap-4">
                                        <div className="h-14 w-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-sm">
                                            <BanknotesIcon className="h-7 w-7" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg md:text-2xl font-black text-[#18216D] tracking-tight italic">Financial Framework</h3>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Tuition & Dues Breakdown</p>
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100 shadow-sm hidden sm:block">Financial Status</span>
                                </div>
                                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-1">Total Outstanding Balance</p>
                                        <h4 className="text-4xl md:text-5xl font-black text-[#18216D] tracking-tighter">
                                            KES {childFinances?.summary?.balance?.toLocaleString() || '0'}
                                        </h4>
                                        {childFinances?.summary?.credit_balance > 0 && (
                                            <div className="mt-4 flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl w-fit border border-emerald-100">
                                                <i className="fas fa-wallet text-[10px]"></i>
                                                <span className="text-[10px] font-black uppercase tracking-widest">Credit Wallet: KES {childFinances.summary.credit_balance.toLocaleString()}</span>
                                            </div>
                                        )}
                                        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                            <button
                                                onClick={() => setShowPaymentModal(true)}
                                                disabled={!selectedChild?.student_id}
                                                className={`${selectedChild?.student_id ? 'bg-[#18216D] text-white hover:bg-indigo-700 shadow-lg shadow-indigo-900/20' : 'bg-slate-200 text-slate-400 cursor-not-allowed'} px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all`}
                                                title={!selectedChild?.student_id ? "This student does not have an admission number assigned yet." : "Make Payment"}
                                            >
                                                Make Payment
                                            </button>
                                            <button
                                                onClick={() => navigate(`/parent/child/${selectedChild.id}/finances`)}
                                                className="text-[10px] font-black uppercase tracking-widest text-[#18216D] hover:underline"
                                            >
                                                View Statement
                                            </button>
                                        </div>
                                    </div>

                                    {childFinances?.fees?.length > 0 && (
                                        <div className="bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100/50">
                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                                Current Term — Fee Breakdown
                                            </div>
                                            <div className="space-y-3">
                                                {childFinances.fees.map((fee, i) => (
                                                    <div key={i} className="flex justify-between items-center text-xs font-bold border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                                                        <span className="text-slate-500">{fee.description || fee.feeCode}</span>
                                                        <span className="text-[#18216D]">
                                                            {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(fee.amount)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex justify-between items-center text-sm mt-4 pt-4 border-t-2 border-emerald-100">
                                                <span className="font-bold text-emerald-600/70 text-xs uppercase tracking-wider">Total Billed</span>
                                                <span className="font-black text-emerald-600 text-lg">KES {childFinances?.summary?.total_fees?.toLocaleString() || 0}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Mastery Hub (1/3) */}
                            <div className="bg-[#18216D] rounded-[2.5rem] shadow-2xl shadow-indigo-900/20 p-6 md:p-10 text-white flex flex-col justify-between relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:scale-125 transition-transform duration-700"></div>
                                <div className="relative z-10 flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FFC425] mb-6">Learning Areas</p>
                                    <div className="space-y-4 mb-8">
                                        {(childMetrics?.learning_areas || [])
                                            .filter(area => area.area_average_pct > 0)
                                            .sort((a, b) => (b.area_average_pct || 0) - (a.area_average_pct || 0))
                                            .slice(0, 5)
                                            .map((area, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-xs text-white border-b border-white/10 pb-3 last:border-0 last:pb-0">
                                                    <span className="font-bold truncate pr-4">{area.name}</span>
                                                    <span className="px-3 py-1 bg-white/10 rounded-full font-black text-[#FFC425] text-[9px] tracking-widest shrink-0">
                                                        {area.area_competency_level_8 || 'N/A'}
                                                    </span>
                                                </div>
                                            ))}
                                        {(!childMetrics?.learning_areas || childMetrics.learning_areas.length === 0) && (
                                            <p className="text-indigo-200/60 text-xs font-medium leading-relaxed italic">
                                                No assessments recorded for this term.
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="relative z-10 flex flex-col gap-4 mt-auto">
                                    <button
                                        onClick={() => navigate(`/parent/child/${selectedChild.id}/progress`)}
                                        className="w-full bg-[#FFC425] text-[#18216D] py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:scale-[1.03] active:scale-95 transition-all shadow-xl shadow-amber-900/20"
                                    >
                                        Full Report View
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Performance Comparison Graph — hidden on mobile */}
                        <div className="hidden md:block mb-8 bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 p-6 md:p-8 sm:p-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                                <div>
                                    <h3 className="text-xl font-black text-[#18216D] flex items-center gap-3 italic">
                                        <ChartBarIcon className="h-6 w-6 text-[#FFC425]" />
                                        Performance Comparison
                                    </h3>
                                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Comparing academic growth across periods</p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <CompareControls onFilterChange={setComparisonFilters} />
                                </div>
                            </div>
                            <div className="h-[400px] w-full overflow-x-auto scrollbar-hide pb-2">
                                <div className="min-w-[600px] h-full">
                                {compLoading ? (
                                    <div className="h-full w-full flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
                                    </div>
                                ) : childComparisonData && childComparisonData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={childComparisonData}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="learning_area"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                                angle={-45}
                                                textAnchor="end"
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                                domain={[0, 100]}
                                            />
                                            <Tooltip
                                                content={({ active, payload, label }) => {
                                                    if (active && payload && payload.length) {
                                                        const current = payload.find(p => p.dataKey === 'current_term');
                                                        const previous = payload.find(p => p.dataKey === 'previous_term');
                                                        return (
                                                            <div className="bg-white p-4 rounded-2xl shadow-xl border border-slate-100 min-w-[180px]">
                                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-50 pb-2 italic">{label}</p>
                                                                <div className="space-y-4">
                                                                    {current && (
                                                                        <div className="flex flex-col">
                                                                            <p className="text-[9px] font-black text-[#18216D] uppercase tracking-tight mb-1">Current Period</p>
                                                                            <div className="flex items-baseline gap-2">
                                                                                <span className="text-xl font-black text-[#18216D] italic tracking-tighter">{calculate8Level(current.value, performanceBands)}</span>
                                                                                <span className="text-[10px] font-bold text-slate-400">({calculateMeanScore(current.value, performanceBands).toFixed(2)} MS)</span>
                                                                            </div>
                                                                            <p className="text-[10px] font-bold text-slate-500 mt-1">{current.value}% Mastery</p>
                                                                        </div>
                                                                    )}
                                                                    {previous && (
                                                                        <div className="flex flex-col pt-3 border-t border-slate-50">
                                                                            <p className="text-[9px] font-black text-[#B48A1B] uppercase tracking-tight mb-1">Previous Period</p>
                                                                            <div className="flex items-baseline gap-2">
                                                                                <span className="text-xl font-black text-[#FFC425] italic tracking-tighter">{calculate8Level(previous.value, performanceBands)}</span>
                                                                                <span className="text-[10px] font-bold text-[#FFC425]">({calculateMeanScore(previous.value, performanceBands).toFixed(2)} MS)</span>
                                                                            </div>
                                                                            <p className="text-[10px] font-bold text-[#FFC425] mt-1">{previous.value}% Mastery</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                            <Bar
                                                name={`Current: ${childComparisonData[0]?.current_term_name || 'Term'}`}
                                                dataKey="current_term"
                                                fill="#18216D"
                                                radius={[4, 4, 0, 0]}
                                                barSize={30}
                                            />
                                            <Bar
                                                name={`Previous: ${childComparisonData[0]?.previous_term_name || 'Term'}`}
                                                dataKey="previous_term"
                                                fill="#FFC425"
                                                radius={[4, 4, 0, 0]}
                                                barSize={30}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-slate-400 font-bold italic text-sm">
                                        Insufficient historical data for performance comparison.
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>

                        {/* Row 3: Calendar (2/3) and Recent Activities (1/3) */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                            {/* Calendar (2/3) */}
                            <div className="lg:col-span-2 bg-white rounded-[2.5rem] p-6 md:p-10 border border-slate-100 shadow-xl shadow-indigo-900/5">
                                <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
                                    <div>
                                        <h3 className="text-xl md:text-2xl font-black text-[#18216D] tracking-tight flex items-center gap-3 italic">
                                            <CalendarIcon className="h-6 w-6 text-[#FFC425]" />
                                            Scholar Timeline
                                        </h3>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Deadlines & Upcoming Assessments</p>
                                    </div>
                                    <div className="flex items-center justify-between md:justify-center gap-2 md:gap-4 bg-slate-50 p-2 rounded-2xl border border-slate-100 shadow-inner w-full md:w-auto">
                                        <button onClick={() => changeMonth(-1)} className="p-2 md:p-2.5 hover:bg-white rounded-xl transition-all shadow-sm text-slate-400 hover:text-[#18216D]">
                                            <ChevronLeftIcon className="w-5 h-5" />
                                        </button>
                                        <span className="text-xs md:text-sm font-black text-[#18216D] uppercase tracking-widest flex-1 md:min-w-[140px] text-center italic">
                                            {currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                                        </span>
                                        <button onClick={() => changeMonth(1)} className="p-2 md:p-2.5 hover:bg-white rounded-xl transition-all shadow-sm text-slate-400 hover:text-[#18216D]">
                                            <ChevronRightIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-7 gap-6">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                        <div key={day} className="text-center text-[10px] font-black text-slate-300 uppercase tracking-widest pb-4">
                                            {day}
                                        </div>
                                    ))}
                                    {daysInMonth.map((dateObj, idx) => {
                                        const hasEvents = dateObj.fullDate && eventDates[dateObj.fullDate];

                                        // Robust check for today in local time
                                        const today = new Date();
                                        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                        const isToday = dateObj.fullDate === todayStr;

                                        // Helper for ordinal suffix (1st, 2nd, 3rd, 4th)
                                        const getOrdinalSuffix = (n) => {
                                            const s = ["th", "st", "nd", "rd"];
                                            const v = n % 100;
                                            return n + (s[(v - 20) % 10] || s[v] || s[0]);
                                        };

                                        return (
                                            <div 
                                                key={idx} 
                                                onClick={() => {
                                                    if (hasEvents) {
                                                        setSelectedMobileDate(dateObj.fullDate);
                                                    }
                                                }}
                                                className={`relative aspect-square flex flex-col items-center justify-center rounded-2xl transition-all ${dateObj.day ? 'hover:bg-slate-50 cursor-pointer group' : ''} ${isToday ? 'bg-[#18216D] text-white shadow-xl shadow-indigo-900/20 scale-105 z-10' : 'text-[#18216D]'}`}
                                            >
                                                {dateObj.day && (
                                                    <>
                                                        <span className={`text-base font-black ${isToday ? 'text-white' : 'text-[#18216D]'}`}>{dateObj.day}</span>
                                                        {hasEvents && (
                                                            <div className="flex gap-1 mt-1.5">
                                                                {eventDates[dateObj.fullDate].slice(0, 3).map((ev, i) => (
                                                                    <div key={i} className={`w-1.5 h-1.5 rounded-full ${ev.is_completed ? 'bg-emerald-500' : 'bg-[#FFC425]'}`} />
                                                                ))}
                                                            </div>
                                                        )}
                                                        {hasEvents && (
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 z-[100] hidden md:block invisible md:group-hover:visible bg-[#18216D] text-white p-5 rounded-[1.5rem] shadow-2xl border border-white/10 min-w-[240px] animate-in zoom-in-95 duration-200">
                                                                <p className="text-[9px] font-black text-indigo-300 uppercase tracking-widest mb-4 border-b border-white/10 pb-2 italic">
                                                                    Tasks for {getOrdinalSuffix(dateObj.day)}
                                                                </p>
                                                                <div className="space-y-3">
                                                                    {eventDates[dateObj.fullDate].map((ev, i) => (
                                                                        <div key={i} className="flex items-start gap-3 group/item">
                                                                            <div className={`mt-1 shrink-0 h-4 w-4 bg-white/10 rounded-md flex items-center justify-center`}>
                                                                                {ev.is_completed ? <CheckCircleIconSolid className="text-emerald-400 w-3 h-3" /> : <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />}
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="text-[11px] font-black leading-tight italic truncate">{ev.title}</p>
                                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                                    <span className="text-[8px] font-bold text-indigo-300 uppercase tracking-widest">{ev.type}</span>
                                                                                    {ev.is_event && ev.start_datetime && (
                                                                                        <span className="text-[8px] font-bold text-[#FFC425] uppercase tracking-widest">
                                                                                            • {new Date(ev.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="absolute top-full left-1/2 -ml-2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-[#18216D]"></div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Recent Activities (1/3) */}
                            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 p-6 md:p-10 flex flex-col group h-full">
                                <div className="flex items-center justify-between mb-10">
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 bg-[#FFC425]/10 rounded-2xl flex items-center justify-center border border-[#FFC425]/20 group-hover:rotate-6 transition-transform">
                                            <ClockIcon className="h-6 w-6 text-[#B48A1B]" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-[#18216D] italic tracking-tight">Recent Activity</h3>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Live status updates</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6 flex-1 overflow-y-auto custom-scrollbar pr-2">
                                    {childActivities ? (
                                        [
                                            ...(childActivities.assignments || []).map(a => ({ ...a, type: 'Assignment' })),
                                            ...(childActivities.quizzes || []).map(q => ({ ...q, type: 'Quiz' })),
                                            ...(childActivities.events || []).map(e => ({ ...e, type: 'School Event', is_event: true }))
                                        ].sort((a, b) => new Date(b.created_at || b.start_datetime) - new Date(a.created_at || a.start_datetime)).slice(0, 5).map((item, idx) => (
                                            <div key={idx} className="flex gap-4 items-start p-4 hover:bg-slate-50 transition-all rounded-2xl group/act">
                                                <div className={`mt-1 h-12 w-12 flex-shrink-0 rounded-xl flex items-center justify-center transition-all group-hover/act:scale-110 shadow-sm ${item.type === 'Quiz' ? 'bg-amber-50 text-amber-600' : (item.is_event ? 'bg-indigo-50 text-indigo-600' : 'bg-blue-50 text-blue-600')}`}>
                                                    {item.type === 'Quiz' ? <AcademicCapIcon className="w-6 h-6" /> : (item.is_event ? <CalendarIcon className="w-6 h-6" /> : <ChartBarIcon className="w-6 h-6" />)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                            {item.type}
                                                            {item.is_completed && <CheckCircleIconSolid className="w-3 h-3 text-emerald-500" />}
                                                        </span>
                                                        <span className="text-[9px] font-black text-slate-300 uppercase">
                                                            {item.is_event ? (
                                                                <>
                                                                    {new Date(item.start_datetime).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                                    {item.start_datetime && ` • ${new Date(item.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                                                </>
                                                            ) : (
                                                                item.due_date ? `Due ${new Date(item.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : new Date(item.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })
                                                            )}
                                                        </span>
                                                    </div>
                                                    <h4 className="font-bold text-[#18216D] text-sm truncate leading-tight">
                                                        {item.title}
                                                    </h4>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        {item.learning_area_name && (
                                                            <span className="text-[8px] font-black text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                                {item.learning_area_name}
                                                            </span>
                                                        )}
                                                        {item.is_completed && <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">Status: Completed</span>}
                                                        {item.is_event && item.cost > 0 && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePayEventFee(item);
                                                                }}
                                                                className="text-[8px] font-black uppercase tracking-widest bg-emerald-600 text-white px-2 py-1 rounded shadow-sm hover:scale-105 active:scale-95 transition-all"
                                                            >
                                                                Pay Fee (KES {item.cost})
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-20 text-slate-300 font-bold uppercase tracking-[0.2em] text-[10px] italic">Fetching live scholar updates...</div>
                                    )}

                                    {childActivities && ([...childActivities.assignments, ...childActivities.quizzes].length === 0) && (
                                        <div className="text-center py-20 text-slate-300 font-bold uppercase tracking-widest text-[10px]">No activities recorded</div>
                                    )}
                                </div>


                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Mobile Calendar Bottom Sheet */}
            {selectedMobileDate && (
                <div className="fixed inset-0 z-[300] flex items-end justify-center md:hidden p-0">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedMobileDate(null)}></div>
                    <div className="relative bg-[#18216D] rounded-t-[2.5rem] w-full shadow-2xl overflow-hidden animate-in slide-in-from-bottom-full duration-300 max-h-[85vh] flex flex-col">
                        <div className="w-full flex justify-center pt-4 pb-2 absolute top-0 left-0 right-0 z-50 bg-[#18216D]">
                            <div className="w-12 h-1.5 bg-white/20 rounded-full"></div>
                        </div>
                        <div className="p-6 pt-10 overflow-y-auto custom-scrollbar">
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-4 border-b border-white/10 pb-2 italic">
                                Tasks for {new Date(selectedMobileDate).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                            </p>
                            <div className="space-y-4">
                                {(eventDates[selectedMobileDate] || []).map((ev, i) => (
                                    <div key={i} className="flex items-start gap-3 group/item">
                                        <div className={`mt-1 shrink-0 h-4 w-4 bg-white/10 rounded-md flex items-center justify-center`}>
                                            {ev.is_completed ? <CheckCircleIconSolid className="text-emerald-400 w-3 h-3" /> : <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[12px] font-black leading-tight italic truncate text-white">{ev.title}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[9px] font-bold text-indigo-300 uppercase tracking-widest">{ev.type}</span>
                                                {ev.is_event && ev.start_datetime && (
                                                    <span className="text-[9px] font-bold text-[#FFC425] uppercase tracking-widest">
                                                        • {new Date(ev.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!eventDates[selectedMobileDate] || eventDates[selectedMobileDate].length === 0) && (
                                    <p className="text-indigo-200 text-xs italic">No tasks scheduled.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Make Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                studentData={selectedChild}
                balance={childFinances?.summary?.balance || 0}
                onPaymentSuccess={handlePaymentSuccess}
            />

            <FloatingManualButton url="/manuals/Parent_Portal_User_Manual.pdf" />
        </div>
    );
};

export default ParentDashboard;
